import { RecipeData } from "../api/recipeSimulator";
import { colorMap } from "./utils";

/**
 * Ordered color palette derived from the project-wide colorMap.
 * Keys are sorted numerically to guarantee a stable, deterministic order.
 */
export const CHART_COLOR_PALETTE: string[] = Object.keys(colorMap)
  .sort((a, b) => Number(a) - Number(b))
  .map(key => colorMap[Number(key)]);

/** Stripe angle (radians) for each process index. Wraps after 8. */
const PROCESS_STRIPE_ANGLES = [
  Math.PI / 4,   // 45° forward
  -Math.PI / 4,  // 45° backward
  0,             // horizontal
  Math.PI / 2,   // vertical
  Math.PI / 6,   // 30°
  -Math.PI / 6,  // -30°
  Math.PI / 3,   // 60°
  -Math.PI / 3,  // -60°
] as const;

/**
 * Creates a repeating canvas tile that paints semi-transparent stripes at a
 * unique angle over `baseColor`.  The result is passed directly to ECharts as
 * `itemStyle.color` (ECharts accepts `{ image, repeat }` as a canvas pattern).
 *
 * This approach is completely independent of ECharts' aria.decal system, so
 * ingredient bars that receive a plain hex color are never touched by any
 * automatic pattern engine.
 *
 * Falls back to `baseColor` when the Canvas API is unavailable (e.g. test
 * environments running in jsdom without canvas support).
 */
export function createProcessPatternColor(
  baseColor: string,
  patternIndex: number
): string | { image: HTMLCanvasElement; repeat: string } {
  if (typeof document === "undefined") return baseColor;

  const canvas = document.createElement("canvas");
  const SIZE = 12;
  canvas.width = SIZE;
  canvas.height = SIZE;

  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext("2d");
  } catch {
    return baseColor;
  }

  if (!ctx) return baseColor;

  // Fill the tile with the base colour
  ctx.fillStyle = baseColor;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Draw a single stripe across the tile at the chosen angle
  const angle = PROCESS_STRIPE_ANGLES[patternIndex % PROCESS_STRIPE_ANGLES.length];
  ctx.save();
  ctx.translate(SIZE / 2, SIZE / 2);
  ctx.rotate(angle);
  ctx.strokeStyle = "rgba(255,255,255,0.6)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-SIZE, 0);
  ctx.lineTo(SIZE, 0);
  ctx.stroke();
  ctx.restore();

  return { image: canvas, repeat: "repeat" };
}

/**
 * Returns the first color in CHART_COLOR_PALETTE that is not already present
 * in `usedColors`.  Comparison is case-insensitive.
 *
 * If every palette color is already used (edge case: more items than palette
 * entries), the function wraps around so a color is always returned.
 */
export function pickNextColor(usedColors: string[]): string {
  const usedSet = new Set(usedColors.map(c => c.toLowerCase()));

  const available = CHART_COLOR_PALETTE.find(c => !usedSet.has(c.toLowerCase()));
  if (available) return available;

  // Wrap-around: cycle through the palette using modulo
  return CHART_COLOR_PALETTE[usedColors.length % CHART_COLOR_PALETTE.length];
}

/**
 * Collects every `chartColor` value that is already assigned across all
 * ingredients and processes in a recipe (original recipe + all variants).
 *
 * Used to determine which palette colors are taken before assigning a new one.
 */
export function extractUsedColors(recipeData: RecipeData | null | undefined): string[] {
  if (!recipeData) return [];

  const colors: string[] = [];

  const collectFrom = (items: any[] | undefined) => {
    items?.forEach(item => {
      if (item?.chartColor) colors.push(item.chartColor);
    });
  };

  const { originalRecipe } = recipeData;
  if (originalRecipe) {
    collectFrom(originalRecipe.calculatedProductSalesUnit);
    collectFrom(originalRecipe.genericIngredient);
    collectFrom(originalRecipe.calculatedIngredient);
    collectFrom(originalRecipe.processes);
  }

  recipeData.variants?.forEach(variant => {
    collectFrom(variant.calculatedProductSalesUnit);
    collectFrom(variant.genericIngredient);
    collectFrom(variant.calculatedIngredient);
    collectFrom(variant.processes);
  });

  return colors;
}
